import React from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi, locationsApi } from '../../api/services';
import type {
  EdgeOnboardCustomerUser,
  EdgeOnboardGoLive,
  EdgeOnboardGoLiveState,
  EdgeOnboardReviewState,
  Location,
} from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import {
  compareEoValues,
  EoEmpty,
  EoIcon,
  EoPage,
  EoPageHeader,
  EoSortHeader,
  formatEoDate,
  GoLiveStateChip,
  HexahqAccountLink,
  nextEoSort,
  ReviewStateChip,
  useEoSnackbar,
  type EoSortState,
} from './eoShared';
import { SlackMentionsDialog } from './SlackMentionsDialog';

const GO_LIVE_STATES: EdgeOnboardGoLiveState[] = ['Not Started', 'In Progress', 'Complete'];
// Both ladders are ordered by PROGRESS, and sorting uses the index — sorting
// these columns as text would rank "Complete" first, which reads backwards.
const REVIEW_STATES: EdgeOnboardReviewState[] = ['Not Started', 'Under Review', 'Complete'];

type SortKey = 'location' | 'user' | 'state' | 'review' | 'subs' | 'slips' | 'created';

interface GoLiveColumn {
  key: SortKey;
  label: string;
  align?: 'center';
  title?: string;
}

const COLUMNS: GoLiveColumn[] = [
  { key: 'location', label: 'Location' },
  { key: 'user', label: 'Customer User' },
  { key: 'state', label: 'State' },
  { key: 'review', label: 'Review' },
  { key: 'subs', label: 'Subs', align: 'center', title: 'Submissions' },
  { key: 'slips', label: 'Slips', align: 'center', title: 'Tag slips' },
  { key: 'created', label: 'Created' },
];

// "Finished" is BOTH tracks. A go-live that has finished tagging but still reads
// Under Review is exactly the row CS needs to see, so it is not hidden.
const isFullyComplete = (g: EdgeOnboardGoLive) =>
  g.state === 'Complete' && g.review_state === 'Complete';

// The two label helpers below are what the cells render AND what sorting reads,
// so a sort can't disagree with the text on screen.
const locationLabel = (g: EdgeOnboardGoLive, locationsById: Map<number, Location>) =>
  locationsById.get(g.location_id)?.name ?? `Location #${g.location_id}`;

const userLabel = (user: EdgeOnboardCustomerUser | undefined) =>
  user ? `${user.first_name} ${user.last_name ?? ''}`.trim() : null;

// One go-live per location at a time; the location name opens the detail page.
export const GoLivesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, snackbarElement } = useEoSnackbar();

  const navigate = useNavigate();
  // ?create=1 deep link (e.g. from the Users page's "Create go-live" action)
  // lands with the create dialog already open.
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = React.useState('');
  const [stateFilter, setStateFilter] = React.useState<EdgeOnboardGoLiveState | null>(null);
  const [stateMenuAnchor, setStateMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [showCompleted, setShowCompleted] = React.useState(false);
  // null = the server's own ordering, until someone picks a column.
  const [sort, setSort] = React.useState<EoSortState<SortKey> | null>(null);
  const [createOpen, setCreateOpen] = React.useState(() => searchParams.get('create') === '1');
  // Slack @-mention settings for onboarding notifications (sc-6383).
  const [slackSettingsOpen, setSlackSettingsOpen] = React.useState(false);

  React.useEffect(() => {
    if (searchParams.get('create') === '1') {
      // Consume the param so refresh/back doesn't re-open the dialog.
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goLivesQuery = useQuery({
    queryKey: ['edge-onboard', 'go-lives'],
    queryFn: () => edgeOnboardApi.goLives.getAll(),
  });
  const locationsQuery = useQuery({
    queryKey: ['edge-onboard', 'locations-all'],
    queryFn: () => locationsApi.getAll(),
  });
  // include_deleted so a go-live assigned to a since-deleted user still shows
  // who it belongs to (marked "(deleted)") instead of a blank cell.
  const usersQuery = useQuery({
    queryKey: ['edge-onboard', 'customer-users', 'all'],
    queryFn: () => edgeOnboardApi.customerUsers.getAll({ include_deleted: true }),
  });

  const locationsById = React.useMemo(() => {
    const map = new Map<number, Location>();
    (locationsQuery.data ?? []).forEach(l => map.set(l.id, l));
    return map;
  }, [locationsQuery.data]);

  const usersById = React.useMemo(() => {
    const map = new Map<number, EdgeOnboardCustomerUser>();
    (usersQuery.data ?? []).forEach(u => map.set(u.id, u));
    return map;
  }, [usersQuery.data]);

  // Explicitly asking for State: Complete beats the hide-completed default —
  // honoring the default there would show an empty table with no visible reason.
  const includeCompleted = showCompleted || stateFilter === 'Complete';

  const sortValue = React.useCallback(
    (g: EdgeOnboardGoLive, key: SortKey): string | number | null => {
      switch (key) {
        case 'location':
          return locationLabel(g, locationsById).toLowerCase();
        case 'user':
          return userLabel(usersById.get(g.customer_user_id))?.toLowerCase() ?? null;
        case 'state':
          return GO_LIVE_STATES.indexOf(g.state);
        case 'review':
          return REVIEW_STATES.indexOf(g.review_state);
        case 'subs':
          return g.counts?.submissions ?? null;
        case 'slips':
          return g.counts?.tag_slips_total ?? null;
        case 'created': {
          const at = g.created_at ? Date.parse(g.created_at) : NaN;
          return Number.isNaN(at) ? null : at;
        }
        default:
          return null;
      }
    },
    [locationsById, usersById],
  );

  const rows = React.useMemo(() => {
    let list = goLivesQuery.data ?? [];
    if (!includeCompleted) list = list.filter(g => !isFullyComplete(g));
    if (stateFilter) list = list.filter(g => g.state === stateFilter);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(g => {
        const loc = locationsById.get(g.location_id);
        const user = usersById.get(g.customer_user_id);
        return (
          loc?.name.toLowerCase().includes(term) ||
          (user?.email ?? '').toLowerCase().includes(term) ||
          (user?.username ?? '').toLowerCase().includes(term) ||
          `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.toLowerCase().includes(term)
        );
      });
    }
    if (sort) {
      // Copy first — the query cache's array must not be sorted in place. The id
      // tiebreak keeps equal values in a fixed order across re-renders.
      list = [...list].sort(
        (a, b) => compareEoValues(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir) || a.id - b.id,
      );
    }
    return list;
  }, [goLivesQuery.data, includeCompleted, stateFilter, search, locationsById, usersById, sort, sortValue]);

  // Drives the empty-state copy: rows the completed default is holding back.
  const hiddenCompletedCount = React.useMemo(
    () => (includeCompleted ? 0 : (goLivesQuery.data ?? []).filter(isFullyComplete).length),
    [goLivesQuery.data, includeCompleted],
  );

  const isLoading = goLivesQuery.isLoading || locationsQuery.isLoading || usersQuery.isLoading;
  const loadError = goLivesQuery.error || locationsQuery.error || usersQuery.error;

  const totalCount = goLivesQuery.data?.length ?? 0;
  const filtersActive = Boolean(stateFilter) || search.trim().length > 0;
  // Three distinct dead ends, three distinct explanations: nothing exists yet,
  // everything that exists is finished and hidden, or the filters exclude it all.
  const emptyMessage =
    totalCount === 0
      ? 'No go-lives yet — create one to start an onboarding.'
      : !filtersActive && hiddenCompletedCount > 0
        ? hiddenCompletedCount === 1
          ? 'The only go-live here is complete and reviewed — turn on “Show completed go-lives” to see it.'
          : `All ${hiddenCompletedCount} go-lives here are complete and reviewed — turn on “Show completed go-lives” to see them.`
        : hiddenCompletedCount > 0
          ? `No go-lives match the current filters. ${hiddenCompletedCount} completed go-live${hiddenCompletedCount === 1 ? ' is' : 's are'} hidden — turn on “Show completed go-lives” to include them.`
          : 'No go-lives match the current filters.';

  return (
    <EoPage>
      <EoPageHeader
        title="Go-Lives"
        sub="Scheduled installs. One per location, assigned to a customer user."
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="ea-icon-btn"
              aria-label="Slack notification settings"
              title="Slack notification settings"
              onClick={() => setSlackSettingsOpen(true)}
            >
              <EoIcon name="settings" />
            </button>
            <button className="ea-btn contained" onClick={() => setCreateOpen(true)}>
              <EoIcon name="add" />
              Create Go-Live
            </button>
          </div>
        }
      />

      <div className="ea-filter-bar">
        <div className="ea-filter wide">
          <EoIcon name="search" />
          <input
            placeholder="Search location or user…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search go-lives"
          />
        </div>
        <button
          className={`ea-filter${stateFilter ? ' has-value' : ''}`}
          onClick={e => setStateMenuAnchor(e.currentTarget)}
          aria-haspopup="menu"
          aria-label="Filter by state"
        >
          {stateFilter ? (
            <>
              <span className="label-prefix">State:</span>
              {stateFilter}
              <span
                className="ea-ico clear"
                role="button"
                aria-label="Clear state filter"
                onClick={e => {
                  e.stopPropagation();
                  setStateFilter(null);
                }}
              >
                close
              </span>
            </>
          ) : (
            <>
              <EoIcon name="filter_list" />
              State
              <EoIcon name="expand_more" />
            </>
          )}
        </button>
        <Menu
          anchorEl={stateMenuAnchor}
          open={Boolean(stateMenuAnchor)}
          onClose={() => setStateMenuAnchor(null)}
        >
          {GO_LIVE_STATES.map(s => (
            <MenuItem
              key={s}
              selected={s === stateFilter}
              onClick={() => {
                setStateFilter(s);
                setStateMenuAnchor(null);
              }}
            >
              {s}
            </MenuItem>
          ))}
        </Menu>
        <button
          className={`ea-filter${showCompleted ? ' has-value' : ''}`}
          aria-label="Show completed go-lives"
          aria-pressed={showCompleted}
          onClick={() => setShowCompleted(v => !v)}
          title={
            stateFilter === 'Complete'
              ? 'The State: Complete filter already shows completed go-lives.'
              : 'Completed go-lives (tagging and review both Complete) are hidden by default.'
          }
        >
          <EoIcon name={showCompleted ? 'check_box' : 'check_box_outline_blank'} />
          Show completed
        </button>
        <div className="spacer" />
        <span className="results">
          <strong>{rows.length}</strong> go-live{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {loadError ? (
        <Alert severity="error">{extractApiError(loadError, 'Failed to load go-lives')}</Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="ea-paper" style={{ overflow: 'hidden' }}>
          {rows.length === 0 ? (
            <EoEmpty icon="bolt" message={emptyMessage} />
          ) : (
            <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  {COLUMNS.map(col => (
                    <EoSortHeader<SortKey>
                      key={col.key}
                      label={col.label}
                      sortKey={col.key}
                      sort={sort}
                      onSort={key => setSort(current => nextEoSort(current, key))}
                      align={col.align}
                      title={col.title}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(g => {
                  const loc = locationsById.get(g.location_id);
                  const user = usersById.get(g.customer_user_id);
                  return (
                    <tr key={g.id}>
                      <td>
                        <RouterLink className="edge-link with-arrow" to={`/onboarding/go_lives/${g.id}`}>
                          <span className="edge-link__text">{locationLabel(g, locationsById)}</span>
                          <span className="edge-link__arrow">arrow_forward</span>
                        </RouterLink>
                        <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>
                          {loc?.hexahq_account_id != null ? (
                            <HexahqAccountLink accountId={loc.hexahq_account_id} />
                          ) : (
                            '—'
                          )}
                        </div>
                      </td>
                      <td>
                        {user ? (
                          <>
                            {userLabel(user)}{' '}
                            {user.deleted_at && <span className="muted">(deleted) </span>}
                            <span className="muted mono">· {user.email ?? user.username}</span>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td><GoLiveStateChip state={g.state} /></td>
                      <td><ReviewStateChip state={g.review_state} /></td>
                      <td style={{ textAlign: 'center' }}>{g.counts?.submissions ?? '—'}</td>
                      <td style={{ textAlign: 'center' }}>{g.counts?.tag_slips_total ?? '—'}</td>
                      <td className="muted">{formatEoDate(g.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      <CreateGoLiveDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        locations={locationsQuery.data ?? []}
        users={usersQuery.data ?? []}
        goLives={goLivesQuery.data ?? []}
        onCreated={created => {
          setCreateOpen(false);
          // Next step in the setup flow is loading tag slips — hand them a
          // one-click jump with the go-live preselected.
          showSuccess('Go-live created', {
            label: 'Add tag slips',
            onClick: () => navigate(`/onboarding/tag_slips?go_live_id=${created.id}`),
          });
          void queryClient.invalidateQueries({ queryKey: ['edge-onboard', 'go-lives'] });
        }}
      />
      <SlackMentionsDialog
        open={slackSettingsOpen}
        onClose={() => setSlackSettingsOpen(false)}
        onSaved={() => {
          setSlackSettingsOpen(false);
          showSuccess('Slack notification settings saved');
        }}
      />
      {snackbarElement}
    </EoPage>
  );
};

interface CreateGoLiveDialogProps {
  open: boolean;
  onClose: () => void;
  locations: Location[];
  users: EdgeOnboardCustomerUser[];
  goLives: EdgeOnboardGoLive[];
  onCreated: (created: EdgeOnboardGoLive) => void;
}

export const CreateGoLiveDialog: React.FC<CreateGoLiveDialogProps> = ({
  open,
  onClose,
  locations,
  users,
  goLives,
  onCreated,
}) => {
  const [location, setLocation] = React.useState<Location | null>(null);
  const [user, setUser] = React.useState<EdgeOnboardCustomerUser | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Reset per open so a cancelled draft doesn't leak into the next create.
  React.useEffect(() => {
    if (open) {
      setLocation(null);
      setUser(null);
      setError(null);
    }
  }, [open]);

  const activeGoLiveLocationIds = React.useMemo(
    () => new Set(goLives.filter(g => g.state !== 'Complete').map(g => g.location_id)),
    [goLives],
  );

  // "Scoped to users at the selected location."
  const scopedUsers = React.useMemo(
    () => (location ? users.filter(u => u.location_id === location.id && !u.deleted_at) : []),
    [users, location],
  );

  const createMutation = useMutation({
    mutationFn: (data: { location_id: number; customer_user_id: number }) =>
      edgeOnboardApi.goLives.create(data),
    onSuccess: created => onCreated(created),
    onError: err => setError(extractApiError(err, 'Failed to create go-live')),
  });

  const disabledReason = (l: Location): string | null => {
    if (!l.hexahq_account_id) return 'Link an HQ Account to enable';
    if (activeGoLiveLocationIds.has(l.id)) return 'An active go-live already exists here';
    return null;
  };

  return (
    // Dialogs portal to <body>, outside the page's .eo-scope wrapper — the
    // paper re-applies the scope class so ea-* styles work inside.
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: 'eo-scope' }}>
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Create Go-Live
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Autocomplete
          options={locations}
          value={location}
          onChange={(_, value) => {
            setLocation(value);
            setUser(null);
          }}
          getOptionLabel={l => l.name}
          getOptionDisabled={l => disabledReason(l) !== null}
          renderOption={(props, l) => {
            const reason = disabledReason(l);
            return (
              <Tooltip key={l.id} title={reason ?? ''} placement="right">
                {/* span keeps the tooltip live on disabled options */}
                <span>
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1 }}>{l.name}</Box>
                      {l.hexahq_account_id ? (
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, fontFamily: 'monospace' }}>
                          HQ #{l.hexahq_account_id}
                        </Box>
                      ) : (
                        <Box component="span" sx={{ color: 'warning.main', fontSize: 12 }}>
                          Not linked
                        </Box>
                      )}
                    </Box>
                  </li>
                </span>
              </Tooltip>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Location"
              required
              helperText="Unlinked locations are disabled — link an HQ Account from the Locations page first."
            />
          )}
        />
        <Autocomplete
          options={scopedUsers}
          value={user}
          onChange={(_, value) => setUser(value)}
          disabled={!location}
          getOptionLabel={u => `${u.first_name} ${u.last_name ?? ''}${u.email || u.username ? ` (${u.email ?? u.username})` : ''}`.trim()}
          noOptionsText={location ? 'No customer users at this location — add one on the Users page.' : ''}
          renderInput={params => (
            <TextField
              {...params}
              label="Customer User"
              required
              helperText="Scoped to users at the selected location."
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!location || !user || createMutation.isPending}
          onClick={() => {
            if (!location || !user) return;
            setError(null);
            createMutation.mutate({ location_id: location.id, customer_user_id: user.id });
          }}
        >
          {createMutation.isPending ? 'Creating…' : 'Create Go-Live'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
